import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Check, ArrowRight, Sparkles, Info, HeartHandshake, BookOpen, Bot, Rocket, Copy, MessageCircle, Upload } from "lucide-react";
import { useConfig } from "@/contexts/ConfigContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const CLP = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

// ═════════════════════════════════════════════════════════════
// Planes v4 ColegaLink (Feb 2026) — 100% CLP nativo (sin UF).
// 4 tiers: Free · Pionero (reserva $15.000) · Pionero Fundador · Premium.
// ═════════════════════════════════════════════════════════════
const PLAN_UI = {
  free: {
    subtitle: "Nivel por defecto",
    icon: "sparkles",
    ctaLabel: "Crear cuenta gratis",
    ctaTo: "/register",
  },
  pionero: {
    subtitle: "Validación MVP · primeros 50 abogados",
    icon: "rocket",
    ctaLabel: "Reservar mi cupo Pionero",
    ctaTo: "/app/planes/pionero",
  },
  embajador_pionero: {
    subtitle: "Beneficios vitalicios · sólo para Pioneros que cumplieron el reto",
    icon: "crown",
    ctaLabel: "Suscribirme como Fundador",
    ctaTo: "/app/planes/pionero-fundador",
  },
  premium: {
    subtitle: "Para abogados desde el usuario 51",
    icon: "sparkles",
    ctaLabel: "Suscribirme a Premium",
    ctaTo: "/app/planes/premium",
  },
};

export default function Pricing() {
  const { config } = useConfig();
  const [billing, setBilling] = useState("annual"); // global toggle
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/plans")
      .then((r) => { if (!cancelled) setCatalog(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const mediationFixedClp = catalog?.mediation_fixed_charge_clp || 0;

  return (
    <div className="container-app py-16 max-w-6xl" data-testid="pricing-page">
      <section className="text-center">
        <div className="label-eyebrow">MVP · Colega Link</div>
        <h1 className="serif text-5xl md:text-6xl mt-4 leading-[0.95] tracking-tight">
          Planes para delegar<br className="hidden md:block"/>
          <span className="brand-gradient-text">con tranquilidad.</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          El colega recibe el 100% de su honorario. Tú conoces el total antes de contratar.
        </p>

        <Link to="/app/asistente-ia" className="mt-6 inline-flex items-center gap-3 panel px-5 py-3 border-cyan-400/30 hover:bg-cyan-400/5 transition-colors" data-testid="pricing-ai-highlight">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400/30 to-emerald-400/20 border border-cyan-400/40 flex items-center justify-center">
            <Sparkles size={14} className="text-cyan-300"/>
          </span>
          <span className="text-left">
            <span className="block text-xs label-eyebrow text-cyan-300">Colega IA · Asistente jurídico</span>
            <span className="block text-sm font-medium">
              {catalog?.ai_included_queries_per_month || 50} consultas/mes · pack extra {catalog?.ai_extra_pack_queries || 20} por {CLP(catalog?.ai_extra_pack_clp || 9990)}
            </span>
            <span className="block mt-1 text-[11px] text-cyan-100/75">
              Cada consulta incluye aclaraciones y complementos del mismo asunto.
            </span>
          </span>
          <ArrowRight size={14} className="text-cyan-300"/>
        </Link>

        {/* Global monthly/annual toggle */}
        <div className="mt-8 inline-flex bg-white/5 border border-white/10 rounded-full p-0.5" data-testid="billing-toggle-global">
          <button
            type="button"
            onClick={() => setBilling("annual")}
            className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${billing === "annual" ? "bg-amber-400 text-amber-950" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="billing-annual-global"
          >
            Anual · Ahorra 20%
          </button>
          <button
            type="button"
            onClick={() => setBilling("monthly")}
            className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${billing === "monthly" ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="billing-monthly-global"
          >
            Mensual
          </button>
        </div>
      </section>

      <section className="mt-10 grid md:grid-cols-2 xl:grid-cols-4 gap-5 max-w-7xl mx-auto">
        {loading && (
          <div className="col-span-full text-center text-muted-foreground text-sm">Cargando planes…</div>
        )}
        {catalog?.plans?.map((plan) => (
          <PlanCard key={plan.code} plan={plan} billing={billing} catalog={catalog} />
        ))}
      </section>

      {(config.new_family_mediation_enabled || config.new_mediation_enabled) && (
        <section className="mt-8 max-w-3xl mx-auto" data-testid="pricing-mediacion-familiar-cta">
          <Link to="/mediacion-familiar" className="panel p-5 border-cyan-400/25 hover:bg-cyan-400/5 transition-colors flex items-center gap-4 group">
            <HeartHandshake size={22} className="text-cyan-300 shrink-0"/>
            <div className="grow">
              <div className="label-eyebrow text-cyan-300">Producto separado</div>
              <div className="serif text-lg mt-1">Mediación Familiar Privada</div>
              <div className="text-xs text-muted-foreground mt-1">
                Cargo fijo ColegaLink de <strong>{CLP(config.colega_link_initial_fee_clp || 13200)}</strong> al inicio,
                y sólo <strong>{Math.round((config.colega_link_agreement_rate || 0.08) * 10000) / 100}% de comisión sobre el saldo</strong> si se firma acuerdo. Tope total {CLP(config.family_mediation_total_cap_clp || 130602)} para la persona solicitante.
              </div>
            </div>
            <ArrowRight size={14} className="text-cyan-300 group-hover:translate-x-0.5 transition-transform"/>
          </Link>
        </section>
      )}

      <section className="mt-10 max-w-3xl mx-auto space-y-4 text-sm text-muted-foreground text-center">
        <p className="p-4 panel">
          <strong className="text-foreground">El cargo Colega Link se paga sólo cuando contratas una delegación.</strong>{" "}
          El colega recibe íntegro el honorario que definió.
        </p>
        <p className="text-xs inline-flex items-start gap-2">
          <Info size={12} className="text-muted-foreground mt-0.5 shrink-0" />
          <span>Precios expresados en pesos chilenos (CLP). El monto real de la transferencia se calcula al momento de confirmar la compra.</span>
        </p>
      </section>

      <ExampleCalculation catalog={catalog} />

      <section className="mt-20 max-w-3xl mx-auto">
        <div className="label-eyebrow text-center">Preguntas frecuentes</div>
        <h2 className="serif text-3xl mt-3 text-center">Antes de contratar</h2>
        <div className="mt-8 space-y-3">
          <Faq q="¿Cómo se calcula el cargo Colega Link?">
            El cargo es un porcentaje del honorario que el colega delegado definió. Con plan Free el cargo es 12%; con Premium baja a 7% y con Pionero Fundador es 0% vitalicio. Lo paga el mandante, adicional al honorario.
          </Faq>
          <Faq q="¿Cuántas consultas de Colega IA incluye mi plan?">
            {catalog?.ai_included_queries_per_month || 50} consultas al mes en cualquier plan pagado (Pionero durante el reto, Pionero Fundador y Premium). Una consulta incluye todas las aclaraciones y hasta tres complementos del mismo asunto durante 30 minutos. Si necesitas más, puedes comprar un pack extra de {catalog?.ai_extra_pack_queries || 20} consultas por {CLP(catalog?.ai_extra_pack_clp || 0)}.
          </Faq>
          <Faq q="¿Hay un cargo mínimo por operación?">
            En Free se aplica un piso interno para cubrir costos operativos. En Premium hay un piso mínimo por gestión. <strong className="text-amber-200">Pionero Fundador está exento del piso mínimo</strong> como beneficio de su membresía.
          </Faq>
          <Faq q="¿El colega delegado recibe menos que el honorario que puso?">
            No. El delegado recibe siempre el 100% de su honorario. El cargo Colega Link nunca se le descuenta.
          </Faq>
          <Faq q="¿Puedo cancelar mi membresía?">
            Sí, en cualquier momento. Si cancelas antes del término anual, mantienes tu tarifa hasta el vencimiento del período pagado; luego pasa a Free (12%).
          </Faq>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mt-20 panel p-10 md:p-14 text-center relative overflow-hidden ring-1 ring-amber-400/30 bg-gradient-to-br from-amber-950/20 via-background to-background">
        <div className="relative">
          <h2 className="serif text-4xl md:text-5xl leading-tight">No suspendas una audiencia por falta de colega.</h2>
          <Link to="/planes" className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-md text-sm font-medium bg-amber-500 hover:bg-amber-400 text-amber-950" data-testid="pricing-final-cta">
            Ver planes y beneficios <ArrowRight size={14}/>
          </Link>
        </div>
      </section>
    </div>
  );
}

function PlanCard({ plan, billing, catalog }) {
  const ui = PLAN_UI[plan.code] || {};
  const highlight = plan.highlight;
  const soldOut = plan.code === "pionero" && plan.sold_out;

  // Precio por ciclo (100% CLP)
  const priceClpMonthly = plan.price_clp_monthly;
  const priceClpAnnual = plan.price_clp_annual;
  const priceClpAnnualWithCredit = plan.price_clp_annual_with_credit;

  return (
    <div
      className={`panel p-8 relative flex flex-col ${highlight ? "ring-2 ring-amber-400/50 bg-gradient-to-br from-amber-950/20 to-background" : ""}`}
      data-testid={`plan-${plan.code}`}
    >
      {plan.badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider ${
          plan.code === "pionero" ? "bg-amber-400 text-amber-950" :
          plan.code === "embajador_pionero" ? "bg-emerald-400 text-emerald-950" :
          plan.code === "premium" ? "bg-cyan-400 text-cyan-950" :
          "bg-white/20 text-white"
        }`}>
          {plan.badge}
        </div>
      )}

      <div className="flex items-center gap-2">
        {plan.code === "pionero" ? <Rocket size={18} className="text-amber-400"/> :
         highlight ? <Crown size={18} className="text-amber-400" /> :
         <Sparkles size={18} className="text-[hsl(var(--accent))]" />}
        <span className={`label-eyebrow text-[10px] ${highlight ? "text-amber-300" : ""}`}>{ui.subtitle}</span>
      </div>

      <h3 className="serif text-3xl mt-3 leading-tight">{plan.name}</h3>

      <div className="mt-5">
        {plan.code === "free" ? (
          <div className="serif text-5xl">Gratis</div>
        ) : plan.code === "pionero" ? (
          <>
            <div className="serif text-5xl" data-testid={`price-onetime-${plan.code}`}>
              {CLP(catalog?.pionero_initial_fee_clp || plan.initial_fee_clp)}
              <span className="text-base text-muted-foreground"> · reserva única</span>
            </div>
            <div className="mt-1 text-xs text-emerald-300">💡 Se abona al plan Pionero Fundador anual (si cumples el reto)</div>
            {plan.pionero_remaining !== undefined && (
              <div className={`mt-2 text-xs font-semibold ${soldOut ? "text-red-300" : "text-amber-300"}`} data-testid="pionero-slots-left">
                {soldOut ? "🚫 Cupos agotados" : `🔥 ${plan.pionero_remaining}/${plan.cap} cupos disponibles`}
              </div>
            )}
          </>
        ) : billing === "monthly" && priceClpMonthly ? (
          <>
            <div className="serif text-5xl" data-testid={`price-monthly-${plan.code}`}>
              {CLP(priceClpMonthly)}
              <span className="text-base text-muted-foreground"> / mes</span>
            </div>
            {priceClpAnnual && (
              <div className="mt-1 text-xs text-amber-300 font-medium" data-testid={`monthly-savings-${plan.code}`}>
                💡 Anual ahorra 20%
              </div>
            )}
          </>
        ) : (
          <>
            <div className="serif text-5xl" data-testid={`price-annual-${plan.code}`}>
              {CLP(priceClpAnnualWithCredit || priceClpAnnual)}
              <span className="text-base text-muted-foreground"> / año</span>
            </div>
            {priceClpAnnualWithCredit && priceClpAnnualWithCredit < priceClpAnnual ? (
              <div className="mt-1 text-xs text-muted-foreground" data-testid={`price-annual-note-${plan.code}`}>
                Con abono Pionero (ahorras {CLP(priceClpAnnual - priceClpAnnualWithCredit)})
              </div>
            ) : (
              <div className="mt-1 text-xs text-muted-foreground" data-testid={`price-annual-note-${plan.code}`}>
                Ahorra 20% vs mensual
              </div>
            )}
            {priceClpMonthly && (
              <div className="mt-1 text-xs text-emerald-300" data-testid={`monthly-equivalent-${plan.code}`}>
                Equivale a {CLP(Math.round((priceClpAnnualWithCredit || priceClpAnnual) / 12))}/mes
              </div>
            )}
          </>
        )}
      </div>

      {/* Banner exclusivo IA + Biblioteca */}
      {(plan.ai_access || plan.library_access) && (
        <div className="mt-4 p-3 rounded-md border border-cyan-400/25 bg-gradient-to-br from-cyan-950/30 to-emerald-950/20" data-testid={`benefits-${plan.code}`}>
          <div className="text-[10px] label-eyebrow text-cyan-300 mb-1.5">BENEFICIOS EXCLUSIVOS</div>
          <div className="flex flex-wrap gap-2">
            {plan.ai_access && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-cyan-400/10 border border-cyan-400/30 text-cyan-200">
                <Bot size={11}/> Colega IA · {catalog?.ai_included_queries_per_month || 50}/mes
              </span>
            )}
            {plan.library_access && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-400/10 border border-emerald-400/30 text-emerald-200">
                <BookOpen size={11}/> Biblioteca Jurídica
              </span>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 p-3 rounded-md bg-white/5 border border-white/10">
        <div className="text-[10px] label-eyebrow">Cargo Pago Protegido</div>
        <div className="mt-1 text-2xl font-medium">
          {plan.platform_fee_pct}% <span className="text-xs text-muted-foreground font-normal">del honorario del colega</span>
        </div>
        {plan.code === "premium" && catalog?.premium_min_fee_clp && (
          <div className="mt-1 text-[11px] text-muted-foreground">
            Mínimo por gestión: <strong className="text-foreground">{CLP(catalog.premium_min_fee_clp)}</strong>
          </div>
        )}
      </div>

      <ul className="mt-6 space-y-2 text-sm flex-1">
        {plan.perks.map((perk) => (
          <li key={perk} className="flex items-start gap-2">
            <Check size={14} className={`mt-0.5 shrink-0 ${highlight ? "text-amber-400" : "text-[hsl(var(--accent))]"}`} />
            <span>{perk}</span>
          </li>
        ))}
      </ul>

      {plan.limitations && plan.limitations.length > 0 && (
        <ul className="mt-4 space-y-1 text-xs text-muted-foreground border-t border-white/5 pt-4">
          {plan.limitations.map((l) => <li key={l}>· {l}</li>)}
        </ul>
      )}

      <PlanCTA plan={plan} billing={billing} soldOut={soldOut} ui={ui}/>
    </div>
  );
}

function PlanCTA({ plan, billing, soldOut, ui }) {
  const [modalOpen, setModalOpen] = useState(false);
  const { user } = useAuth();

  const isFree = plan.code === "free";
  const isLawyer = user?.role === "lawyer";
  const disabled = soldOut;

  if (!user) {
    return (
      <Link
        to={isFree ? "/register" : `/register?next=${encodeURIComponent("/planes")}`}
        className={`mt-6 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
          plan.highlight ? "bg-amber-500 hover:bg-amber-400 text-amber-950" : "border border-white/15 hover:bg-white/5"
        }`}
        data-testid={`plan-${plan.code}-cta`}
      >
        {isFree ? (ui.ctaLabel || "Crear cuenta") : "Regístrate para suscribirte"} <ArrowRight size={14} />
      </Link>
    );
  }

  if (!isLawyer) {
    if (isFree) return null;
    return (
      <div className="mt-6 text-xs text-muted-foreground text-center px-3 py-2 rounded-md border border-white/10 bg-white/5">
        Este plan es exclusivo para abogados.
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && !isFree && setModalOpen(true)}
        disabled={disabled || isFree}
        className={`mt-6 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          plan.highlight ? "bg-amber-500 hover:bg-amber-400 text-amber-950" : "border border-white/15 hover:bg-white/5"
        }`}
        data-testid={`plan-${plan.code}-cta`}
      >
        {soldOut ? "Cupos agotados" : isFree ? "Tu plan actual" : (ui.ctaLabel || "Suscribirme")} {!disabled && <ArrowRight size={14} />}
      </button>
      {modalOpen && <PurchaseModal plan={plan} billing={billing} onClose={() => setModalOpen(false)}/>}
    </>
  );
}

function PurchaseModal({ plan, billing, onClose }) {
  const [cycle, setCycle] = useState(plan.code === "pionero" ? null : billing);
  const [txRef, setTxRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const endpoint = plan.code === "pionero"
        ? "/plans/pionero/purchase"
        : plan.code === "embajador_pionero"
          ? "/plans/embajador/purchase"
          : "/plans/premium/purchase";
      const body = { transfer_reference: txRef.trim() || undefined };
      if (plan.code !== "pionero") body.billing_cycle = cycle;
      const r = await api.post(endpoint, body);
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const amount = result?.amount_clp
    ?? (plan.code === "pionero" ? plan.initial_fee_clp
        : cycle === "annual" ? (plan.price_clp_annual_with_credit || plan.price_clp_annual)
        : plan.price_clp_monthly);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="purchase-modal">
      <div className="panel max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow text-amber-300">Suscripción</div>
            <h3 className="serif text-2xl mt-1">{plan.name}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="purchase-modal-close">✕</button>
        </div>

        {result ? (
          <TransferInstructions result={result} onClose={onClose} plan={plan} />
        ) : (
          <div className="mt-6 space-y-4">
            {plan.code !== "pionero" && (
              <div>
                <div className="text-xs label-eyebrow mb-2">Ciclo de facturación</div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setCycle("annual")} className={`px-3 py-2 rounded-md border text-sm ${cycle === "annual" ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-white/10 hover:bg-white/5"}`} data-testid="cycle-annual">
                    Anual · {CLP(plan.price_clp_annual_with_credit || plan.price_clp_annual)}
                  </button>
                  <button onClick={() => setCycle("monthly")} className={`px-3 py-2 rounded-md border text-sm ${cycle === "monthly" ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-white/10 hover:bg-white/5"}`} data-testid="cycle-monthly">
                    Mensual · {CLP(plan.price_clp_monthly)}
                  </button>
                </div>
              </div>
            )}
            <div className="p-3 rounded-md bg-white/5 border border-white/10 flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">Total a transferir</span>
              <span className="serif text-3xl">{CLP(amount || 0)}</span>
            </div>
            <label className="block">
              <span className="text-xs label-eyebrow">N° de referencia de transferencia (opcional)</span>
              <input type="text" value={txRef} onChange={(e) => setTxRef(e.target.value)} maxLength={80}
                className="field mt-1.5 w-full" placeholder="Ej: 12345678" data-testid="tx-ref"/>
            </label>
            {error && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="purchase-error">{error}</div>}
            <button onClick={submit} disabled={submitting || (plan.code !== "pionero" && !cycle)} className="btn-primary w-full disabled:opacity-50" data-testid="purchase-submit">
              {submitting ? "Registrando…" : "Registrar compra"}
            </button>
            <p className="text-[10px] text-muted-foreground text-center">
              Realizarás una transferencia manual. Nuestro equipo confirmará el pago y activará tu plan.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function TransferInstructions({ result, onClose, plan }) {
  const { config } = useConfig();
  const bank = config?.bank_account || {};
  const wa = (config?.admin_whatsapp || "").replace(/[^+\d]/g, "");
  const ref = (result.payment_id || "").slice(0, 8).toUpperCase();
  const amount = result.amount_clp;
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const plainText = [
    `Transferencia a ColegaLink · plan ${plan.name}`,
    `Referencia (OBLIGATORIA en el mensaje): ${ref}`,
    `Monto: ${CLP(amount)}`,
    "",
    `Banco: ${bank.bank_name}`,
    `Titular: ${bank.holder}`,
    `RUT: ${bank.holder_rut}`,
    `${bank.account_type}: ${bank.account_number}`,
    `Email para comprobante: ${bank.email}`,
  ].join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true); setTimeout(() => setCopied(false), 2200);
    } catch { /* noop */ }
  };

  const whatsappUrl = wa
    ? `https://wa.me/${wa.replace(/\+/, "")}?text=${encodeURIComponent(
        `Hola, soy ${result.user_name || ""}. Adjunto comprobante de transferencia por ${CLP(amount)} para el plan ${plan.name}. Referencia: ${ref}`
      )}`
    : null;

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setUploadErr("El archivo supera los 5 MB."); return; }
    setUploading(true); setUploadErr("");
    try {
      const b64 = await new Promise((ok, ko) => {
        const r = new FileReader();
        r.onload = () => ok(String(r.result || "").split(",").pop());
        r.onerror = ko; r.readAsDataURL(file);
      });
      await api.post(`/plans/plan-payments/${result.payment_id}/upload-receipt`, {
        file_b64: b64, file_name: file.name, mime_type: file.type,
      });
      setUploaded(true);
    } catch (e) {
      setUploadErr(e?.response?.data?.detail || e.message || "No se pudo subir.");
    } finally { setUploading(false); }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="transfer-instructions">
      <div className="p-4 rounded-md bg-emerald-400/10 border border-emerald-400/30">
        <div className="text-emerald-200 text-sm font-semibold">✓ Compra registrada — falta transferir</div>
        <div className="text-xs text-muted-foreground mt-1">{result.next_step}</div>
      </div>

      {/* Referencia obligatoria — protagonista */}
      <div className="p-4 rounded-lg border border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-amber-900/20">
        <div className="text-[10px] label-eyebrow text-amber-300">Referencia obligatoria en el mensaje</div>
        <div className="mt-1 flex items-baseline justify-between gap-3">
          <div className="serif text-3xl tracking-widest text-amber-200 font-bold" data-testid="pay-ref">{ref}</div>
          <div className="serif text-2xl text-emerald-300" data-testid="pay-amount">{CLP(amount)}</div>
        </div>
        <div className="mt-2 text-[11px] text-amber-200/80">⚠️ Sin esta referencia no podemos identificar tu pago.</div>
      </div>

      {/* Datos bancarios reales */}
      <div className="p-3 rounded-md bg-white/5 border border-white/10 text-xs space-y-1 font-mono" data-testid="bank-info">
        <BankRow label="Banco" value={bank.bank_name} />
        <BankRow label="Titular" value={bank.holder} />
        <BankRow label="RUT" value={bank.holder_rut} />
        <BankRow label={bank.account_type || "Cuenta"} value={bank.account_number} />
        <BankRow label="Email comprobante" value={bank.email} />
      </div>

      {/* Acciones: copiar + whatsapp */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={copy} className="px-3 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5 inline-flex items-center justify-center gap-2" data-testid="btn-copy-bank">
          {copied ? <><Check size={14} className="text-emerald-300"/> Copiado</> : <><Copy size={14}/> Copiar datos</>}
        </button>
        {whatsappUrl && (
          <a href={whatsappUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-md text-sm bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-medium inline-flex items-center justify-center gap-2" data-testid="btn-whatsapp">
            <MessageCircle size={14}/> Enviarme por WhatsApp
          </a>
        )}
      </div>

      {/* Upload comprobante */}
      <div className="p-3 rounded-md border border-dashed border-cyan-400/30 bg-cyan-400/[0.03]">
        <div className="text-xs label-eyebrow text-cyan-300 inline-flex items-center gap-1"><Upload size={11}/> Subir comprobante (opcional pero acelera aprobación)</div>
        {!uploaded ? (
          <>
            <input
              type="file"
              accept="image/*,application/pdf"
              disabled={uploading}
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="mt-2 block w-full text-xs text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-white/10 file:text-foreground hover:file:bg-white/15 cursor-pointer disabled:opacity-50"
              data-testid="receipt-upload-input"
            />
            {uploadErr && <div className="mt-1.5 text-xs text-red-300" data-testid="receipt-upload-err">{uploadErr}</div>}
            {uploading && <div className="mt-1.5 text-xs text-cyan-300">Subiendo…</div>}
          </>
        ) : (
          <div className="mt-2 text-xs text-emerald-300 inline-flex items-center gap-1.5" data-testid="receipt-uploaded-ok">
            <Check size={12}/> Comprobante recibido. Aprobaremos tu pago en cuanto verifiquemos el depósito.
          </div>
        )}
      </div>

      <button onClick={onClose} className="btn-primary w-full" data-testid="purchase-modal-done">Listo, ya voy a transferir</button>
    </div>
  );
}

function BankRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground font-medium text-right">{value || "—"}</span>
    </div>
  );
}

function ExampleCalculation({ catalog }) {
  const [honorario, setHonorario] = useState(60000);
  const [plan, setPlan] = useState("free");
  const feePct = plan === "premium" ? 7 : plan === "embajador_pionero" ? 0 : 12;
  const MIN_FREE = 30000;
  const premiumMinClp = catalog?.premium_min_fee_clp || 0;
  const feeFromPct = useMemo(() => Math.round(honorario * feePct / 100), [honorario, feePct]);
  const floor = plan === "premium" ? premiumMinClp : plan === "free" ? MIN_FREE : 0;
  const fee = Math.max(feeFromPct, floor);
  const minApplied = fee === floor && floor > feeFromPct && floor > 0;
  const total = honorario + fee;
  return (
    <section className="mt-20 panel p-8 max-w-3xl mx-auto" data-testid="pricing-example">
      <div className="label-eyebrow">Ejemplo transparente</div>
      <h3 className="serif text-2xl mt-2">Cómo se ve el cargo en la vista del mandante</h3>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs label-eyebrow">Honorario del colega</span>
          <input type="number" min={0} step={1000} value={honorario}
            onChange={(e) => setHonorario(Math.max(0, parseInt(e.target.value || "0")))}
            className="field mt-2 w-full" data-testid="example-honorario"/>
        </label>
        <label className="block">
          <span className="text-xs label-eyebrow">Plan del abogado</span>
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="field mt-2 w-full" data-testid="example-plan">
            <option value="free">Colega Free (12%)</option>
            <option value="premium">Premium (7%)</option>
            <option value="embajador_pionero">Pionero Fundador (0% vitalicio)</option>
          </select>
        </label>
      </div>
      <div className="mt-6 p-5 rounded-md bg-white/5 border border-white/10 space-y-2 text-sm">
        <Row label="Honorario del colega" value={CLP(honorario)} />
        <Row label={`Cargo Colega Link (${feePct}%)`} value={CLP(fee)} />
        {minApplied && plan === "premium" && (
          <p className="text-xs text-amber-200/90 p-2 rounded-md bg-amber-400/10 border border-amber-400/25 flex items-start gap-2" data-testid="example-min-notice">
            <Info size={12} className="text-amber-300 mt-0.5 shrink-0"/>
            <span>Mínimo Premium aplicado: {CLP(floor)}. El 7% real sería {CLP(feeFromPct)}.</span>
          </p>
        )}
        {minApplied && plan === "free" && (
          <p className="text-xs text-amber-200/90 p-2 rounded-md bg-amber-400/10 border border-amber-400/25 flex items-start gap-2" data-testid="example-min-notice">
            <Info size={12} className="text-amber-300 mt-0.5 shrink-0"/>
            <span>Piso mínimo Free aplicado: {CLP(floor)}. En Pionero Fundador este piso NO aplica.</span>
          </p>
        )}
        {plan === "embajador_pionero" && (
          <p className="text-xs text-emerald-300 p-2 rounded-md bg-emerald-400/10 border border-emerald-400/25 flex items-start gap-2" data-testid="example-fundador-exempt">
            <Info size={12} className="text-emerald-300 mt-0.5 shrink-0"/>
            <span>✓ Como Pionero Fundador, tu comisión es 0% vitalicia. El mandante paga sólo el honorario del colega.</span>
          </p>
        )}
        <div className="border-t border-white/10 my-2" />
        <Row label="Total a pagar" value={CLP(total)} strong dataTestId="example-total" />
        <Row label="El colega recibe" value={CLP(honorario)} accent="emerald" dataTestId="example-recibe" />
      </div>
      <p className="mt-4 text-xs text-muted-foreground text-center">
        Los valores anteriores son referenciales. En el contrato real verás el desglose exacto antes de confirmar el pago.
      </p>
    </section>
  );
}

function Row({ label, value, strong, accent, dataTestId }) {
  const cls = accent === "emerald" ? "text-emerald-300 font-semibold" : strong ? "text-foreground font-semibold" : "";
  return (
    <div className="flex items-center justify-between" data-testid={dataTestId}>
      <span className={strong ? "font-medium" : "text-muted-foreground"}>{label}</span>
      <span className={cls}>{value}</span>
    </div>
  );
}

function Faq({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <button type="button" onClick={() => setOpen(!open)} className="w-full text-left panel p-4 hover:bg-white/[0.03] transition-colors">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium text-sm">{q}</span>
        <span className={`text-muted-foreground text-lg transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </div>
      {open && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{children}</p>}
    </button>
  );
}
